// Tryb — one question, whatever shape it takes.
//
// Renders a view model from useFieldViews. Every branch is mutually exclusive;
// the view model has already decided which one applies.

import { Badge, Button, Icon, Input, Select, Switch, Tag, Textarea } from "../ds/index.js";

const mono10 = {
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  letterSpacing: "var(--tracking-wide)",
  textTransform: "uppercase",
  color: "var(--text-muted)",
  whiteSpace: "nowrap",
};

export default function TrybField({ f }) {
  if (!f) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0, fontFamily: "var(--font-body)" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
        <div style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--text-primary)", lineHeight: 1.3 }}>{f.label}</div>
        <div style={mono10}>{f.meta}</div>
      </div>

      {f.isInput && (
        <Input type={f.inputType} value={f.value} onChange={f.onChange} placeholder={f.ph} error={f.error ? " " : ""} />
      )}

      {f.isSelect && (
        <Select options={f.options} value={f.value} onChange={f.onChange} placeholder="Choose…" error={f.error ? " " : ""} />
      )}

      {f.isChips && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {f.tags.map((t) => (
            <Tag key={t.label} selected={t.selected} onClick={t.onClick}>
              {t.label}
            </Tag>
          ))}
        </div>
      )}

      {f.isTextarea && (
        <Textarea rows={f.rows} value={f.value} onChange={f.onChange} placeholder={f.ph} error={f.error ? " " : ""} />
      )}

      {f.isSwitch && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, minHeight: 46 }}>
          <Switch checked={f.on} onChange={f.onChange} label={f.onText} />
        </div>
      )}

      {f.isFile && (f.hasFile ? <FilePresent f={f} /> : <FileEmpty f={f} />)}

      {f.isCover && <CoverField f={f} />}

      {f.isRange && (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <Input type="number" value={f.lo} onChange={f.onLo} />
          </div>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>to</span>
          <div style={{ flex: 1 }}>
            <Input type="number" value={f.hi} onChange={f.onHi} />
          </div>
        </div>
      )}

      {f.isPair && (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <Input type={f.pairType} value={f.lo} onChange={f.onLo} placeholder={f.loPh} />
          </div>
          <div style={{ flex: 1 }}>
            <Input type={f.pairType2} value={f.hi} onChange={f.onHi} placeholder={f.hiPh} />
          </div>
        </div>
      )}

      {f.isGeo && <GeoField f={f} />}

      {f.isDates && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, minHeight: 30 }}>
          {f.dates.map((d, i) => (
            <Badge key={`${d.label}-${i}`} tone={d.tone}>
              {d.label}
            </Badge>
          ))}
        </div>
      )}

      {f.isRo && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            minHeight: 46,
            padding: "0 14px",
            borderRadius: "var(--radius-md)",
            background: "var(--surface-sunken)",
            fontFamily: "var(--font-mono)",
            fontSize: "var(--text-sm)",
            color: "var(--text-secondary)",
          }}
        >
          {f.value}
        </div>
      )}

      {f.hasError && <div style={{ fontSize: "var(--text-xs)", color: "var(--crimson-600)", fontWeight: 600 }}>{f.error}</div>}
    </div>
  );
}

function FilePresent({ f }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 10px 10px 12px",
        border: "1px solid var(--border-default)",
        borderRadius: "var(--radius-md)",
        background: "var(--surface-raised)",
      }}
    >
      {f.hasThumb ? (
        <img src={f.thumb} alt="" style={{ width: 44, height: 44, borderRadius: "var(--radius-sm)", objectFit: "cover", flex: "none" }} />
      ) : (
        <span
          style={{
            width: 44,
            height: 44,
            borderRadius: "var(--radius-sm)",
            background: "var(--sage-100)",
            color: "var(--sage-700)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flex: "none",
          }}
        >
          <Icon name="Check" size={20} />
        </span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: "var(--text-sm)",
            fontWeight: 600,
            color: "var(--text-primary)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {f.fileName}
        </div>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}>{f.fileMeta}</div>
      </div>
      <Button variant="ghost" size="sm" onClick={f.onRemove}>
        Remove
      </Button>
    </div>
  );
}

function FileEmpty({ f }) {
  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 12px 12px 14px",
        border: "1px dashed var(--border-strong)",
        borderRadius: "var(--radius-md)",
        background: "var(--surface-card)",
        cursor: "pointer",
      }}
    >
      <span style={{ flex: 1, fontSize: "var(--text-sm)", color: "var(--text-muted)", lineHeight: 1.45 }}>{f.fileHint}</span>
      <Button variant="secondary" size="sm">
        Choose file
      </Button>
      <input
        type="file"
        accept={f.accept}
        onChange={f.onFile}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer" }}
      />
    </div>
  );
}

function CoverField({ f }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 240px) 1fr", gap: 18, alignItems: "center" }}>
      <div
        style={{
          aspectRatio: "3 / 2",
          maxWidth: "100%",
          borderRadius: "var(--radius-lg)",
          overflow: "hidden",
          position: "relative",
          background: f.coverBg,
          color: f.coverFg,
          border: "1px solid var(--border-subtle)",
        }}
      >
        {f.coverIsUpload && f.thumb && (
          <img src={f.thumb} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
        )}
        {f.coverShowIcon && (
          <div style={{ position: "absolute", right: -14, bottom: -18, opacity: 0.22 }}>
            <Icon name={f.coverIcon} size={120} sw={1.3} />
          </div>
        )}
        {f.coverShowText && (
          <div
            style={{
              position: "absolute",
              left: 14,
              right: 14,
              bottom: 12,
              fontFamily: f.coverFont,
              fontWeight: 800,
              fontSize: 20,
              lineHeight: 1.05,
              letterSpacing: "-0.02em",
              textWrap: "balance",
            }}
          >
            {f.coverText}
          </div>
        )}
        {f.coverEmpty && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: "var(--text-muted)",
              border: "1px dashed var(--border-strong)",
              borderRadius: "var(--radius-lg)",
            }}
          >
            No cover yet
          </div>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "flex-start" }}>
        <div style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", lineHeight: 1.5 }}>{f.coverCaption}</div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="primary" size="sm" onClick={f.onOpen}>
            {f.coverCta}
          </Button>
          {f.coverHas && (
            <Button variant="ghost" size="sm" onClick={f.onRemove}>
              Remove
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function GeoField({ f }) {
  return (
    <div
      style={{
        position: "relative",
        height: 180,
        borderRadius: "var(--radius-md)",
        overflow: "hidden",
        border: "1px solid var(--border-default)",
        backgroundColor: "var(--sand-200)",
        backgroundImage:
          "linear-gradient(var(--sand-300) 1px, transparent 1px), linear-gradient(90deg, var(--sand-300) 1px, transparent 1px)",
        backgroundSize: "48px 48px",
      }}
    >
      <div style={{ position: "absolute", left: "18%", top: 0, bottom: 0, width: 14, background: "var(--sand-100)" }} />
      <div style={{ position: "absolute", left: 0, right: 0, top: "58%", height: 22, background: "var(--sand-100)" }} />
      <div
        style={{
          position: "absolute",
          left: "44%",
          top: "22%",
          width: 120,
          height: 70,
          borderRadius: 6,
          background: "var(--stone-100)",
          border: "1px solid var(--stone-200)",
        }}
      />
      {f.hasPin && (
        <div
          style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -100%)", color: "var(--ember-500)", display: "inline-flex" }}
        >
          <Icon name="Pin" size={36} sw={2.4} />
        </div>
      )}
      <div
        style={{ position: "absolute", left: 12, right: 12, bottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}
      >
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "var(--text-xs)",
            color: "var(--text-secondary)",
            background: "var(--surface-card)",
            padding: "6px 10px",
            borderRadius: "var(--radius-pill)",
            border: "1px solid var(--border-subtle)",
          }}
        >
          {f.pinText}
        </span>
        <Button variant="primary" size="sm" onClick={f.onPin}>
          {f.pinCta}
        </Button>
      </div>
    </div>
  );
}

/** The grid cell wrapper — fields declare whether they want one column or two. */
export function FieldCell({ f }) {
  return (
    <div style={{ gridColumn: `span ${f.cols}`, minWidth: 0 }}>
      <TrybField f={f} />
    </div>
  );
}
